import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import styles from "../../css/Dashboard.module.css";
import { Button } from "react-bootstrap";
import Chatbot from "../SharedComponents/Chatbot";

const UniversityDashboard = () => {
  const [jobs, setJobs] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [company, setCompany] = useState("");
  const [location, setLocation] = useState("");
  const [requirements, setRequirements] = useState("");
  const [jobType, setJobType] = useState(""); // Added back jobType for the search bar
  const [sortField, setSortField] = useState(""); // State for sorting field
  const [sortOrder, setSortOrder] = useState("asc"); // State for sort order
  const navigate = useNavigate();

  const filterJobs = (jobs, queryParams) => {
    return jobs.filter((job) => {
      if (
        queryParams.get("title") &&
        !job.title
          .toLowerCase()
          .includes(queryParams.get("title").toLowerCase())
      )
        return false;
      if (
        queryParams.get("company") &&
        !job.company
          .toLowerCase()
          .includes(queryParams.get("company").toLowerCase())
      )
        return false;
      if (
        queryParams.get("location") &&
        !job.location
          .toLowerCase()
          .includes(queryParams.get("location").toLowerCase())
      )
        return false;

      if (queryParams.get("requirements")) {
        const requiredSkills = queryParams
          .get("requirements")
          .toLowerCase()
          .split(",")
          .map((skill) => skill.trim());
        const jobRequirements = Array.isArray(job.requirements)
          ? job.requirements.map((skill) => skill.toLowerCase().trim())
          : [];
        if (!requiredSkills.every((skill) => jobRequirements.includes(skill)))
          return false;
      }

      if (
        queryParams.get("jobType") &&
        job.jobType.toLowerCase() !== queryParams.get("jobType").toLowerCase()
      )
        return false;
      return true;
    });
  };

  useEffect(() => {
    handleSearch();
  }, [sortField, sortOrder]); // Re-run search when sorting options change

  const handleSearch = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        alert("You need to log in first.");
        navigate("/university/login");
        return;
      }

      const queryParams = new URLSearchParams();
      if (searchTerm) queryParams.append("title", searchTerm);
      if (company) queryParams.append("company", company);
      if (location) queryParams.append("location", location);
      if (requirements) queryParams.append("requirements", requirements);
      if (jobType) queryParams.append("jobType", jobType);
      if (sortField) queryParams.append("sortField", sortField);
      if (sortOrder) queryParams.append("sortOrder", sortOrder);

      const response = await axios.get(
        `https://techservicemarket-backend.onrender.com/api/university/jobs?${queryParams.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const filteredJobs = filterJobs(response.data, queryParams);
      console.log(filteredJobs);

      setJobs(filteredJobs);
    } catch (error) {
      console.error("Error searching jobs:", error);
    }
  };

  const handleRepostJob = async (jobId) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        alert("You need to log in first.");
        navigate("/university/login");
        return;
      }

      await axios.post(
        `https://techservicemarket-backend.onrender.com/api/university/jobs/${jobId}/repost`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      alert("Job reposted successfully");
      handleSearch();
    } catch (error) {
      if (
        error.response &&
        error.response.data &&
        error.response.data.message ===
          "This job has already been reposted by you."
      ) {
        alert("You have already reposted this job.");
      } else {
        console.error("Error reposting job:", error);
        alert("An error occurred while reposting the job.");
      }
    }
  };

  const handleSortChange = (field) => {
    // Toggle sort order if the same field is selected, otherwise reset to ascending
    if (sortField === field) {
      setSortOrder((prevOrder) => (prevOrder === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  return (
    <div className={styles.dashboardContainer}>
      <h1 className={styles.dashboardHeader}>University Partner Dashboard</h1>

      {/* Button Container */}
      <div className={styles.buttonContainer}>
        <button
          onClick={() => navigate("/university/jobs/new")}
          className={styles.searchButton}
        >
          Post a New Job
        </button>
        <button
          onClick={() => navigate("/university/jobs/reposted")}
          className={styles.searchButton}
        >
          View Posted/Reposted Jobs
        </button>
        <button
          onClick={() => navigate("/university/students")}
          className={styles.searchButton}
        >
          View Students
        </button>
      </div>

      {/* Search Section */}
      <div className={styles.searchSection}>
        <input
          type="text"
          placeholder="Search by Title"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className={styles.searchInput}
        />
        <input
          type="text"
          placeholder="Search by Company"
          value={company}
          onChange={(e) => setCompany(e.target.value)}
          className={styles.searchInput}
        />
        <input
          type="text"
          placeholder="Search by Location"
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          className={styles.searchInput}
        />
        <input
          type="text"
          placeholder="Requirements (comma-separated)"
          value={requirements}
          onChange={(e) => setRequirements(e.target.value)}
          className={styles.searchInput}
        />
        <select
          value={jobType}
          onChange={(e) => setJobType(e.target.value)}
          className={styles.selectInput}
        >
          <option value="">Select Job Type</option>
          <option value="Full-time">Full-time</option>
          <option value="Part-time">Part-time</option>
          <option value="Contract">Contract</option>
          <option value="Internship">Internship</option>
        </select>
        <button onClick={handleSearch} className={styles.searchButton}>
          Search
        </button>
      </div>

      {/* Sorting Options */}
      <div className={styles.sortSection}>
        <button
          onClick={() => handleSortChange("salary")}
          className={styles.sortButton}
        >
          Sort by Salary{" "}
          {sortField === "salary" &&
            (sortOrder === "asc" ? "(less)" : "(more)")}
        </button>
        <button
          onClick={() => handleSortChange("requirements")}
          className={styles.sortButton}
        >
          Sort by Requirements{" "}
          {sortField === "requirements" &&
            (sortOrder === "asc" ? "(less)" : "(more)")}
        </button>
        <button
          onClick={() => handleSortChange("createdAt")}
          className={styles.sortButton}
        >
          Sort by Job Posted{" "}
          {sortField === "createdAt" &&
            (sortOrder === "asc" ? "(old to new)" : "(new to old)")}
        </button>
      </div>

      {/* Job List Section */}
      <div className={styles.jobListSection}>
        <h2>Jobs Posted by Recruiters</h2>
        {jobs.map((job) => (
          <div key={job._id} className={styles.jobCard}>
            <h3>{job.title}</h3>
            <p>
              <strong>Company:</strong> {job.company}
            </p>
            <p>
              <strong>Location:</strong> {job.location}
            </p>
            <p>
              <strong>Job Type:</strong> {job.jobType}
            </p>
            <p>
              <strong>Requirements:</strong> {job.requirements.join(", ")}
            </p>
            <p>
              <strong>Description:</strong> {job.description}
            </p>
            <p>
              <strong>Salary:</strong>{" "}
              {job.salary ? `$${job.salary}` : "Not specified"}
            </p>
            <p>
              <strong>Date Posted:</strong>{" "}
              {new Date(job.createdAt).toLocaleDateString()}
            </p>

            <div
              className={styles.jobActions}
              style={{ display: "flex", gap: "10px", marginTop: "10px" }}
            >
              <button
                onClick={() => handleRepostJob(job._id)}
                className={styles.searchButton}
                style={{ backgroundColor: "#28a745" }}
              >
                Repost
              </button>
              <Button 
                as={Link} 
                to={`/university/jobs/${job._id}/applications`}
                variant="primary"
              >
                View Applications ({job.applicationCount || 0})
              </Button>
            </div>
          </div>
        ))}
      </div>

      <Chatbot />
    </div>
  );
};

export default UniversityDashboard;
